// Timer error → message mapping (architecture sprint-04 §5.7; RD §3.14, fixes INV #16).
import { ApiClientError } from '@/lib/api';
import { GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE } from '@/lib/query-client';

export const TIMER_CHANGED_MESSAGE = 'The timer changed in another tab or device.';
export const TIMER_ALREADY_RUNNING_MESSAGE = 'A timer is already running.';
export const PROJECT_GONE_MESSAGE = 'The selected project no longer exists. Choose another.';
// Sprint 13 (§8, §14.5): the server's messages, also used client-side (shortcuts, disabled controls).
export const ON_SHIFT_MESSAGE = 'You are on shift.';
export const SHIFT_PROJECT_MESSAGE = 'Use Start Shift for this project.';
// Sprint 11 (§8.8): the server messages, also used client-side before a request is sent.
export const TIMER_PAUSE_DISABLED_MESSAGE = 'Pausing is turned off for this project.';
export const TIMER_SWITCH_DISABLED_MESSAGE = 'Switching is turned off for this project. Stop the timer instead.';
/** The project rules changed under the client: refresh the timer (its rules) and show the message. */
const RULE_CODES = new Set(['TIMER_PAUSE_DISABLED', 'TIMER_SWITCH_DISABLED']);

const HTTP_NOT_FOUND = 404;
const HTTP_TOO_MANY = 429;
const CHANGED_CODES = new Set(['TIMER_CHANGED', 'NO_TIMER']);
const STATE_CODES = new Set(['TIMER_ALREADY_PAUSED', 'TIMER_NOT_PAUSED', 'TIMER_PAUSED']);
const INLINE_CODES = new Set(['START_NOT_TODAY', 'START_IN_FUTURE', 'SWITCH_NOT_TODAY', 'SWITCH_TIME_OUT_OF_RANGE', 'VALIDATION_ERROR']);
const GLOBAL_CODES = new Set(['ACCOUNT_DEACTIVATED', 'SESSION_EXPIRED']);

export interface TimerErrorInfo {
  message: string;
  /** Refetch ['timer'] (the cached snapshot is stale). */
  refetch: boolean;
  /** Show inside the open dialog when there is one; otherwise toast. */
  inline: boolean;
  /** The timer changed under the dialog: close it and toast. */
  closeDialog: boolean;
  /** Hand to handleGlobalError (deactivated / expired session redirect). */
  global: boolean;
  /** 422 PROJECT_ARCHIVED: refresh projects, me and quick-start when it came from a one-click start. */
  projectArchived: boolean;
}

function info(message: string, partial: Partial<TimerErrorInfo> = {}): TimerErrorInfo {
  return { message, refetch: false, inline: false, closeDialog: false, global: false, projectArchived: false, ...partial };
}

export function timerErrorMessage(err: unknown): TimerErrorInfo {
  if (err instanceof ApiClientError) {
    if (GLOBAL_CODES.has(err.code)) return info(err.message, { global: true });
    if (CHANGED_CODES.has(err.code)) return info(TIMER_CHANGED_MESSAGE, { refetch: true, closeDialog: true });
    if (err.code === 'TIMER_ALREADY_RUNNING') return info(TIMER_ALREADY_RUNNING_MESSAGE, { refetch: true, closeDialog: true });
    if (STATE_CODES.has(err.code)) return info(err.message, { refetch: true, closeDialog: true });
    if (INLINE_CODES.has(err.code)) return info(err.message, { inline: true });
    if (err.code === 'PROJECT_ARCHIVED') return info(err.message, { inline: true, projectArchived: true });
    if (RULE_CODES.has(err.code)) return info(err.message, { refetch: true, closeDialog: true });
    // Sprint 13: a shift started elsewhere (refresh: the snapshot carries it); a shift project chosen.
    if (err.code === 'ON_SHIFT') return info(ON_SHIFT_MESSAGE, { refetch: true, closeDialog: true });
    if (err.code === 'SHIFT_PROJECT') return info(SHIFT_PROJECT_MESSAGE, { inline: true, projectArchived: true });
    // Sprint 11: an Admin starting on a project they are not a member of; the message says to join it.
    if (err.code === 'PROJECT_NOT_MEMBER') return info(err.message, { inline: true });
    if (err.statusCode === HTTP_NOT_FOUND) return info(PROJECT_GONE_MESSAGE, { inline: true });
    if (err.statusCode === HTTP_TOO_MANY) return info(err.message);
    return info(GENERIC_ERROR_MESSAGE, { inline: true });
  }
  if (err instanceof TypeError) return info(NETWORK_ERROR_MESSAGE, { inline: true });
  return info(GENERIC_ERROR_MESSAGE, { inline: true });
}
